import { useEffect, useState, type ReactNode } from 'react';
import type { MeDto } from '@scs/schemas';
import { Api } from '../lib/api';
import { navigate, useLocation } from '../lib/router';
import { t } from '../lib/i18n';
import { Logo } from '../lib/ui';

function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="auth-page">
      <div className="card auth-card">
        <div className="brand">
          <Logo size={28} />
          <div>
            <h1>{t('app.name')}</h1>
            <div className="muted">{title}</div>
          </div>
        </div>
        {children}
      </div>
    </main>
  );
}

export function LoginPage({ onLogin }: { onLogin(me: MeDto): void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  useEffect(() => {
    Api.authConfig()
      .then((c) => setCanRegister(c.registration))
      .catch(() => setCanRegister(false));
  }, []);
  return (
    <AuthCard title={t('auth.login')}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            onLogin(await Api.login(email, password));
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span>{t('auth.email')}</span>
          <input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>{t('auth.password')}</span>
          <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {error && <p className="error-text" role="alert">{error}</p>}
        <button className="btn primary" disabled={busy}>
          {t('auth.login')}
        </button>
        <p style={{ textAlign: 'center', marginTop: 14 }}>
          <a href="/forgot" onClick={(e) => { e.preventDefault(); navigate('/forgot'); }}>
            {t('auth.forgot')}
          </a>
        </p>
        {canRegister && (
          <p style={{ textAlign: 'center', marginTop: 4 }}>
            Nemate nalog?{' '}
            <a href="/register" onClick={(e) => { e.preventDefault(); navigate('/register'); }}>
              Napravi nalog
            </a>
          </p>
        )}
      </form>
    </AuthCard>
  );
}

export function RegisterPage({ onRegistered }: { onRegistered(me: MeDto): void }) {
  const [f, setF] = useState({ name: '', email: '', password: '', password2: '', workspaceName: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<boolean | null>(null);
  useEffect(() => {
    Api.authConfig()
      .then((c) => setOpen(c.registration))
      .catch(() => setOpen(false));
  }, []);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <AuthCard title="Novi nalog">
      {open === false ? (
        <>
          <p className="muted">Registracija trenutno nije uključena. Zatražite pozivnicu od administratora radionice.</p>
          <BackToLogin />
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (f.password !== f.password2) return setError('Lozinke se ne poklapaju.');
            setBusy(true);
            try {
              onRegistered(await Api.register({ email: f.email, name: f.name, password: f.password, workspaceName: f.workspaceName }));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="field">
            <span>{t('auth.name')}</span>
            <input className="input" autoComplete="name" value={f.name} onChange={set('name')} required autoFocus maxLength={200} />
          </label>
          <label className="field">
            <span>{t('auth.email')}</span>
            <input className="input" type="email" autoComplete="email" value={f.email} onChange={set('email')} required />
          </label>
          <label className="field">
            <span>{t('auth.newPassword')}</span>
            <input className="input" type="password" autoComplete="new-password" minLength={12} value={f.password} onChange={set('password')} required />
          </label>
          <label className="field">
            <span>Ponovite lozinku</span>
            <input className="input" type="password" autoComplete="new-password" minLength={12} value={f.password2} onChange={set('password2')} required />
          </label>
          <label className="field">
            <span>Naziv radionice (opciono)</span>
            <input className="input" value={f.workspaceName} onChange={set('workspaceName')} maxLength={200} placeholder={f.name ? `Radionica — ${f.name}` : ''} />
          </label>
          {error && <p className="error-text" role="alert">{error}</p>}
          <button className="btn primary" disabled={busy || open === null}>
            Napravi nalog
          </button>
          <BackToLogin />
        </form>
      )}
    </AuthCard>
  );
}

export function ResetRequestPage() {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthCard title={t('auth.resetTitle')}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            setMsg((await Api.resetRequest(email)).message);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <label className="field">
          <span>{t('auth.email')}</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </label>
        {msg && <p className="ok-text">{msg}</p>}
        {error && <p className="error-text">{error}</p>}
        <button className="btn primary">{t('auth.resetSend')}</button>
        <BackToLogin />
      </form>
    </AuthCard>
  );
}

function BackToLogin() {
  return (
    <p style={{ textAlign: 'center', marginTop: 14 }}>
      <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>
        {t('auth.backToLogin')}
      </a>
    </p>
  );
}

export function ResetConfirmPage() {
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  const [pw, setPw] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthCard title={t('auth.resetTitle')}>
      {done ? (
        <>
          <p className="ok-text">{t('auth.passwordSet')}</p>
          <BackToLogin />
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await Api.resetConfirm(token, pw);
              // Remove the token from the address bar / history.
              history.replaceState(null, '', '/reset');
              setDone(true);
            } catch (err) {
              setError((err as Error).message);
            }
          }}
        >
          <label className="field">
            <span>{t('auth.newPassword')}</span>
            <input className="input" type="password" autoComplete="new-password" minLength={12} value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus />
          </label>
          {error && <p className="error-text">{error}</p>}
          <button className="btn primary">{t('auth.setPassword')}</button>
        </form>
      )}
    </AuthCard>
  );
}

export function AcceptInvitePage({ onDone }: { onDone(): Promise<void> }) {
  const { query } = useLocation();
  const token = query.get('token') ?? '';
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthCard title={t('auth.inviteTitle')}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await Api.acceptInvite(token, name, pw);
            await onDone();
            navigate('/', true);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <label className="field">
          <span>{t('auth.name')}</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="field">
          <span>{t('auth.newPassword')}</span>
          <input className="input" type="password" autoComplete="new-password" minLength={12} value={pw} onChange={(e) => setPw(e.target.value)} required />
        </label>
        {error && <p className="error-text">{error}</p>}
        <button className="btn primary">{t('auth.accept')}</button>
      </form>
    </AuthCard>
  );
}
